import axios from 'axios';
import { isNative } from '../capacitorInit';
import type { EventShare, SharedEventGroup, MilestoneSummary, MilestoneDetail, MilestoneContribution, Collage, ContributionAsk, OwnerContributionRequestView } from '../types';

// Native iOS bundle is served from capacitor://localhost, not the real backend
// hostname, so relative /api/* calls would resolve against the local bundle
// (and just hang — there's no network fallback). Use an absolute URL there.
const API_BASE_URL = isNative ? 'https://eventscarousel.com/api' : '/api';

// Same reasoning as API_BASE_URL: window.location.origin is capacitor://localhost
// on native, which is meaningless to a human copying/sharing a link. Any URL a
// user might copy, share, or send to someone outside the app must use the real
// web origin, not the native bundle's local scheme.
export const WEB_ORIGIN = isNative ? 'https://eventscarousel.com' : window.location.origin;

export const api = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

let accessToken: string | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

api.interceptors.request.use(config => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

api.interceptors.request.use(config => {
  const activeAccountId = localStorage.getItem('activeAccountId');
  if (activeAccountId) {
    config.headers['X-Delegated-Account'] = activeAccountId;
  }
  return config;
});

api.interceptors.response.use(
  res => res,
  async error => {
    const original = error.config;
    const isAuthEndpoint = original?.url?.includes('/auth/');
    if (error.response?.status === 401 && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      try {
        const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true });
        accessToken = data.accessToken;
        original.headers.Authorization = `Bearer ${accessToken}`;
        return api(original);
      } catch {
        accessToken = null;
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ─── Member event-shares API ─────────────────────────────────────────────────

export const memberShareApi = {
  getEventIds: (memberId: string) =>
    api.get<{ eventIds: string[] }>(`/account/members/${memberId}/event-shares`).then(r => r.data.eventIds),
  changeRole: (memberId: string, role: 'member' | 'viewer') =>
    api.put(`/account/members/${memberId}`, { role }).then(r => r.data),
  setScope: (memberId: string, scope: 'all' | 'specific', eventIds?: string[]) =>
    api.put(`/account/members/${memberId}/scope`, { scope, eventIds }).then(r => r.data),
};

// ─── Contribution requests ("curated collaboration") API ──────────────────────

export const contributionApi = {
  create: (payload: { eventIds: string[]; memberIds: string[] } & Partial<ContributionAsk>) =>
    api.post<{ results: { eventId: string; memberId: string; status: 'created' | 'failed'; error?: string }[] }>(
      '/events/contribution-requests', payload
    ).then(r => r.data),
  listForEvent: (eventId: string) =>
    api.get<OwnerContributionRequestView[]>(`/events/${eventId}/contribution-requests`).then(r => r.data),
  dismiss: (eventId: string, requestId: string) =>
    api.post(`/events/${eventId}/contribution-requests/${requestId}/dismiss`).then(r => r.data),
  cancel: (eventId: string, requestId: string) =>
    api.delete(`/events/${eventId}/contribution-requests/${requestId}`).then(r => r.data),
};

// ─── Share settings API ───────────────────────────────────────────────────────

export const shareSettingsApi = {
  get: () => api.get<{ shareAllWithFriends: boolean }>('/account/share-settings').then(r => r.data),
  update: (shareAllWithFriends: boolean) =>
    api.put<{ shareAllWithFriends: boolean }>('/account/share-settings', { shareAllWithFriends }).then(r => r.data),
};

// ─── Event sharing API ────────────────────────────────────────────────────────

export const eventShareApi = {
  getShares: (eventId: string) => api.get<EventShare[]>(`/events/${eventId}/shares`).then(r => r.data),
  share: (eventId: string, memberIds: string[]) => api.post(`/events/${eventId}/shares`, { memberIds }),
  unshare: (eventId: string, memberId: string) => api.delete(`/events/${eventId}/shares/${memberId}`),
  getSharedWithMe: () => api.get<{ groups: SharedEventGroup[] }>('/events/shared-with-me?allPhotos=true').then(r => r.data),
};

// ─── Content moderation: reporting + blocking ─────────────────────────────────

export const reportApi = {
  reportComment: (eventId: string, commentId: string, reason: string) =>
    api.post(`/events/${eventId}/comments/${commentId}/report`, { reason }),
};

export interface BlockedUser {
  id: string;
  name: string;
  blockedAt: string;
}

export const blockApi = {
  list: () => api.get<BlockedUser[]>('/users/blocked').then(r => r.data),
  block: (userId: string, reason?: string) => api.post(`/users/${userId}/block`, { reason }),
  unblock: (userId: string) => api.delete(`/users/${userId}/block`),
};

// ─── Milestone Slideshow API ───────────────────────────────────────────────────

export interface MilestonePayload {
  personId: string;
  title: string;
  start?: string;
  end?: string;
  musicTrackId?: string;
}

export const milestoneApi = {
  preview: (personId: string, start?: string, end?: string) =>
    api.get<{ events: MilestoneDetail['events']; totalPhotos: number }>('/events/milestones/preview', {
      params: { personId, start, end },
    }).then(r => r.data),
  list: () => api.get<{ milestones: MilestoneSummary[] }>('/events/milestones').then(r => r.data.milestones),
  get: (id: string) => api.get<MilestoneDetail>(`/events/milestones/${id}`).then(r => r.data),
  create: (payload: MilestonePayload) => api.post<MilestoneDetail>('/events/milestones', payload).then(r => r.data),
  update: (id: string, payload: Partial<Pick<MilestonePayload, 'title' | 'start' | 'end' | 'musicTrackId'>>) =>
    api.put<MilestoneDetail>(`/events/milestones/${id}`, payload).then(r => r.data),
  remove: (id: string) => api.delete(`/events/milestones/${id}`),
  publish: (id: string, expiresAt?: string) =>
    api.post<{ publicUrl: string }>(`/events/milestones/${id}/publish`, { expiresAt }).then(r => r.data),
  unpublish: (id: string) => api.delete(`/events/milestones/${id}/publish`),
  listContributions: (id: string) =>
    api.get<{ contributions: MilestoneContribution[] }>(`/events/milestones/${id}/contributions`).then(r => r.data.contributions),
  approveContribution: (id: string, contributionId: string, body: { takenAt?: string; eventId?: string }) =>
    api.post(`/events/milestones/${id}/contributions/${contributionId}/approve`, body),
  rejectContribution: (id: string, contributionId: string) =>
    api.post(`/events/milestones/${id}/contributions/${contributionId}/reject`),
};

// ─── Collage Creator API ───────────────────────────────────────────────────────

export interface CollageItemInput {
  eventPhotoId: string;
  position: number;
  caption?: string | null;
  sourceAudioId?: string | null;
  focalX?: number;
  focalY?: number;
}

export interface CollageTextLayerInput {
  text: string;
  xPct: number;
  yPct: number;
  size: 's' | 'm' | 'l';
  color: string;
}

export interface CollagePayload {
  title?: string | null;
  type: 'grid' | 'storyboard';
  layoutId?: string | null;
  themeId: string;
  items: CollageItemInput[];
  textLayers?: CollageTextLayerInput[];
}

export const collageApi = {
  list: () => api.get<{ collages: Collage[] }>('/events/collages').then(r => r.data.collages),
  get: (id: string) => api.get<Collage>(`/events/collages/${id}`).then(r => r.data),
  create: (payload: CollagePayload) => api.post<Collage>('/events/collages', payload).then(r => r.data),
  update: (id: string, payload: Partial<CollagePayload>) => api.put<Collage>(`/events/collages/${id}`, payload).then(r => r.data),
  remove: (id: string) => api.delete(`/events/collages/${id}`),
  // Field name 'image' distinguishes a rendered collage upload from the
  // regular photo-upload route's 'photo' field, though both share the same
  // backend multer/savePhoto() plumbing.
  uploadRender: (id: string, blob: Blob) => {
    const form = new FormData();
    form.append('image', blob, 'collage.png');
    return api.post<{ renderedImageUrl: string }>(`/events/collages/${id}/render`, form).then(r => r.data);
  },
  publish: (id: string, expiresAt?: string) =>
    api.post<{ publicUrl: string }>(`/events/collages/${id}/publish`, { expiresAt }).then(r => r.data),
  unpublish: (id: string) => api.delete(`/events/collages/${id}/publish`),
};

export const publicCollageApi = {
  get: (token: string) => api.get<Collage>(`/events/public/collages/${token}`).then(r => r.data),
};
